import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogin, adminVerifyOTP } from '../../api/adminApi';
import { useAdminAuth } from '../../context/AdminAuthContext';

const cyan = '#00e5ff';

const inputStyle = {
  width: '100%',
  background: 'rgba(0,229,255,0.04)',
  border: '1px solid rgba(0,229,255,0.2)',
  borderRadius: 8,
  padding: '10px 14px',
  color: '#fff',
  fontSize: 14,
  outline: 'none',
};

const labelStyle = {
  color: '#888',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  display: 'block',
  marginBottom: 6,
};

const RESEND_SECONDS = 120;

const AdminLogin = () => {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const { loginAdmin } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await adminLogin({ identifier, password });
      setStep(2);
      setResendTimer(RESEND_SECONDS);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await adminVerifyOTP({ identifier, otp });
      loginAdmin(res.data.token, res.data.user);
      navigate('/panel/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      await adminLogin({ identifier, password });
      setOtp('');
      setResendTimer(RESEND_SECONDS);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 400, background: '#111', border: '1px solid rgba(0,229,255,0.15)', borderRadius: 14, padding: '36px 32px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>
            One<span style={{ color: cyan }}>Circle</span>
          </div>
          <div style={{ color: '#555', fontSize: 13, marginTop: 4 }}>Admin Panel</div>
        </div>

        {error && (
          <div style={{ background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 20 }}>
            {error}
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleCredentials}>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Email or Admin Code</label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Enter admin email or code"
                style={inputStyle}
                required
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{ width: '100%', padding: '12px', borderRadius: 8, border: 'none', background: loading ? 'rgba(255,255,255,0.06)' : cyan, color: loading ? '#555' : '#000', fontWeight: 700, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Verifying...' : 'Sign In'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyOTP}>
            <div style={{ color: '#888', fontSize: 12, marginBottom: 20, lineHeight: 1.6 }}>
              An OTP has been sent to your registered email. Enter it below to continue.
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>One-Time Password</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                style={{ ...inputStyle, letterSpacing: '0.2em', fontSize: 20, textAlign: 'center' }}
                maxLength={6}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              style={{ width: '100%', padding: '12px', borderRadius: 8, border: 'none', background: (loading || otp.length !== 6) ? 'rgba(255,255,255,0.06)' : cyan, color: (loading || otp.length !== 6) ? '#555' : '#000', fontWeight: 700, fontSize: 15, cursor: (loading || otp.length !== 6) ? 'not-allowed' : 'pointer', marginBottom: 12 }}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>

            {resendTimer > 0 ? (
              <div style={{ textAlign: 'center', color: '#555', fontSize: 12 }}>
                Resend available in <span style={{ color: '#888' }}>{formatTime(resendTimer)}</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={loading}
                style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#888', fontSize: 13, cursor: 'pointer' }}
              >
                Resend OTP
              </button>
            )}

            <button
              type="button"
              onClick={() => { setStep(1); setOtp(''); setError(''); }}
              style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', color: '#555', fontSize: 12, cursor: 'pointer' }}
            >
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
